import * as Intercom from '@intercom/messenger-js-sdk';
import Constants from 'expo-constants';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { captureException } from '@/utils/sentry';
import { getIntercomUserHash, IntercomContext } from './shared';
import { partnerSupplier } from '@/utils/environment';
import { useTheme } from '@/contexts/theme-context';
import type { IntercomIdentity, IntercomProviderProps } from './types';

type BootState = 'idle' | 'booting' | 'booted';

// `Intercom.shutdown()` doesn't unset `window.Intercom`, so its presence is a
// reliable "has the widget script already been injected" signal for the
// boot-vs-default decision — correct even across an operator<->Sales Portal
// experience switch within the same page load, since both share this module.
const isMessengerInjected = (): boolean =>
  typeof window !== 'undefined' &&
  typeof (window as Window & { Intercom?: unknown }).Intercom === 'function';

const buildPayload = (
  identity: IntercomIdentity,
  accentColor: string,
  userHash: string,
  hideDefaultLauncher: boolean,
) => ({
  ...identity.customAttributes,
  action_color: accentColor,
  app_id: Constants.expoConfig!.extra!.intercom.appId,
  background_color: accentColor,
  company: identity.companyId
    ? {
        id: identity.companyId,
        pepper_company_business_organization_id:
          partnerSupplier.businessOrganizationUUID,
      }
    : undefined,
  email: identity.email,
  hide_default_launcher: hideDefaultLauncher,
  user_hash: userHash,
  user_id: identity.userId,
});

export const IntercomContextProvider: React.FC<IntercomProviderProps> = ({
  children,
  hideDefaultLauncher = true,
  identity,
  onBooted,
  showOnBoot = false,
}) => {
  const theme = useTheme();

  // Guards against concurrent boots; reset to 'idle' on failure to allow retry
  const bootStateRef = useRef<BootState>('idle');
  const mountedRef = useRef(true);
  const userHashRef = useRef<string | null>(null);
  // Always-fresh refs read after async gaps, so a boot that started with a
  // stale identity/theme still uses the latest values once the hash resolves
  const identityRef = useRef(identity);
  const accentColorRef = useRef(theme.colors.accent);
  const onBootedRef = useRef(onBooted);
  const hideDefaultLauncherRef = useRef(hideDefaultLauncher);
  const showOnBootRef = useRef(showOnBoot);
  identityRef.current = identity;
  accentColorRef.current = theme.colors.accent;
  onBootedRef.current = onBooted;
  hideDefaultLauncherRef.current = hideDefaultLauncher;
  showOnBootRef.current = showOnBoot;

  const listenersRegisteredRef = useRef(false);
  const visibleRef = useRef(false);
  const [initialized, setInitialized] = useState(false);

  const showCallback = useCallback(() => {
    Intercom.show();
    visibleRef.current = true;

    // Since these listeners can't be registered immediately after
    // initialization, register them after Intercom is first opened
    if (!listenersRegisteredRef.current) {
      Intercom.onHide(() => {
        visibleRef.current = false;
      });
      Intercom.onShow(() => {
        visibleRef.current = true;
      });

      listenersRegisteredRef.current = true;
    }
  }, []);

  useEffect(() => {
    if (!identity) {
      // Identity went away post-boot (e.g. logout, config disabled) — shut
      // down rather than leaving a stale session alive until unmount.
      if (bootStateRef.current === 'booted') {
        Intercom.shutdown();
        bootStateRef.current = 'idle';
        userHashRef.current = null;
        setInitialized(false);
      }
      return;
    }

    if (bootStateRef.current === 'idle') {
      bootStateRef.current = 'booting';

      (async () => {
        try {
          const userHash = await getIntercomUserHash();

          // Unmounted while the hash fetch was in flight — skip the boot
          // entirely rather than booting and immediately shutting down.
          if (!mountedRef.current) {
            bootStateRef.current = 'idle';
            return;
          }
          if (!userHash) {
            bootStateRef.current = 'idle';
            return;
          }

          const freshIdentity = identityRef.current;
          if (!freshIdentity) {
            bootStateRef.current = 'idle';
            return;
          }

          Intercom[isMessengerInjected() ? 'boot' : 'default'](
            buildPayload(
              freshIdentity,
              accentColorRef.current,
              userHash,
              hideDefaultLauncherRef.current,
            ),
          );
          userHashRef.current = userHash;
          bootStateRef.current = 'booted';
          setInitialized(true);
          onBootedRef.current?.();
          if (showOnBootRef.current) {
            showCallback();
          }
        } catch (e) {
          bootStateRef.current = 'idle';
          captureException(e);
        }
      })();

      return;
    }

    if (bootStateRef.current === 'booted' && userHashRef.current) {
      // Identity (or theme) changed post-boot — update in place, no re-boot
      // and no remount required.
      Intercom.update(
        buildPayload(
          identity,
          theme.colors.accent,
          userHashRef.current,
          hideDefaultLauncherRef.current,
        ),
      );
    }
    // bootStateRef.current === 'booting': an initial boot is in flight; it
    // will pick up identityRef.current (the latest value) when it resolves.
  }, [identity, theme, showCallback]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (bootStateRef.current === 'booted') {
        Intercom.shutdown();
      }
    };
  }, []);

  const toggleCallback = useCallback(() => {
    if (visibleRef.current) {
      Intercom.hide();
      visibleRef.current = false;
    } else {
      showCallback();
    }
  }, [showCallback]);

  const pingRouteChangeCallback = useCallback(() => {
    if (bootStateRef.current === 'booted') {
      Intercom.update({});
    }
  }, []);

  const value = useMemo(
    () => ({
      initialized,
      pingRouteChange: pingRouteChangeCallback,
      toggle: toggleCallback,
    }),
    [initialized, pingRouteChangeCallback, toggleCallback],
  );

  return (
    <IntercomContext.Provider value={value}>
      {children}
    </IntercomContext.Provider>
  );
};
